import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogContent,
  IconButton,
  makeStyles,
  Tooltip
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import CheckIcon from '@material-ui/icons/Check'
import { smartEndpoints } from '../../../../../api/smartEndpoints'
import { policyLabel } from './policyGroups'

const useStyles = makeStyles(() => ({
  dialogPaper: {
    maxWidth: '720px',
    width: '720px',
    borderRadius: 'var(--border-radius-md, 8px)'
  },
  titleBar: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '20px 24px 14px',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  titleText: {
    fontSize: 'var(--text-size-lg, 18px)',
    fontWeight: 600,
    color: 'var(--color-neutral-900)',
    fontFamily: 'var(--font-mono, monospace)'
  },
  titleSub: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)',
    marginTop: '2px',
    fontWeight: 500
  },
  content: {
    padding: '16px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  hint: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)',
    lineHeight: 1.5
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'var(--text-size-sm)',
    '& th': {
      textAlign: 'left',
      padding: '8px 12px',
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      borderBottom: '1px solid var(--color-neutral-200)',
      backgroundColor: 'var(--color-neutral-050)'
    },
    '& td': {
      padding: '8px 12px',
      borderBottom: '1px solid var(--color-neutral-100)',
      color: 'var(--color-neutral-800)',
      verticalAlign: 'middle'
    },
    '& tr:last-child td': {
      borderBottom: 'none'
    }
  },
  codeCell: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-900)',
    userSelect: 'all',
    wordBreak: 'break-all'
  },
  descCell: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)',
    lineHeight: 1.4
  },
  tagBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600,
    marginRight: '4px'
  },
  tagLive: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  tagNext: {
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-500)'
  },
  tagCustom: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  copyBtn: {
    padding: 4,
    color: 'var(--color-neutral-300)',
    transition: 'color 0.15s ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  copiedBtn: {
    padding: 4,
    color: 'var(--color-success-600, #16a34a)'
  },
  emptyText: {
    padding: '24px 16px',
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  }
}))

function InvocationCodesModal ({ code, onClose }) {
  const classes = useStyles()
  const [copied, setCopied] = useState(null)
  const copyTimerRef = useRef(null)

  useEffect(() => () => clearTimeout(copyTimerRef.current), [])

  const { data: programData } = smartEndpoints.useGetProgram(code)
  const { data: tags } = smartEndpoints.useListProgramTags(code)

  const latestPolicies = useMemo(() => {
    const config = programData?.program?.config || programData?.config || {}
    return Array.isArray(config.allowedPolicies) ? config.allowedPolicies : []
  }, [programData])

  const rows = useMemo(() => {
    if (!latestPolicies.length && !(tags || []).length) return []

    const result = []
    const liveTag = (tags || []).find(t => t.tagName === 'live')
    const customTags = (tags || []).filter(t => t.tagName !== 'live')

    const policiesForTag = (tag) => {
      const cfg = tag?.config || {}
      return Array.isArray(cfg.allowedPolicies) ? cfg.allowedPolicies : []
    }

    if (liveTag) {
      const livePolicies = policiesForTag(liveTag)
      for (const policy of livePolicies) {
        const label = policyLabel(policy)
        result.push({
          code: `se:${code}:${policy}`,
          tag: 'live',
          desc: `Live version (v${liveTag.version}) · ${label} policy`
        })
      }
    }

    for (const policy of latestPolicies) {
      const label = policyLabel(policy)
      result.push({
        code: `se:${code}:${policy}:next`,
        tag: 'next',
        desc: `Latest/preview version · ${label} policy`
      })
    }

    for (const t of customTags) {
      const tagPolicies = policiesForTag(t)
      for (const policy of tagPolicies) {
        const label = policyLabel(policy)
        result.push({
          code: `se:${code}:${policy}:${t.tagName}`,
          tag: t.tagName,
          desc: `"${t.tagName}" tagged version (v${t.version}) · ${label} policy`
        })
      }
    }

    return result
  }, [code, latestPolicies, tags])

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(text)
      clearTimeout(copyTimerRef.current)
      copyTimerRef.current = setTimeout(() => setCopied(null), 1500)
    } catch {
      /* clipboard not available */
    }
  }, [])

  const tagBadgeClass = (tag) => {
    if (tag === 'live') return `${classes.tagBadge} ${classes.tagLive}`
    if (tag === 'next') return `${classes.tagBadge} ${classes.tagNext}`
    return `${classes.tagBadge} ${classes.tagCustom}`
  }

  return (
    <Dialog
      open
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      <div className={classes.titleBar}>
        <div>
          <div className={classes.titleText}>{code}</div>
          <div className={classes.titleSub}>Invocation Codes</div>
        </div>
        <Tooltip title='Close' arrow>
          <IconButton size='small' onClick={onClose}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>

      <DialogContent className={classes.content}>
        <div className={classes.hint}>
          Use these codes in front-end components to invoke this endpoint. Click the copy button to grab one.
        </div>

        {!rows.length ? (
          <div className={classes.emptyText}>
            No policies configured — add at least one policy in Endpoint Configuration to generate invocation codes.
          </div>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Version</th>
                <th>Description</th>
                <th style={{ width: '40px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.code}>
                  <td className={classes.codeCell}>{row.code}</td>
                  <td>
                    <span className={tagBadgeClass(row.tag)}>{row.tag}</span>
                  </td>
                  <td className={classes.descCell}>{row.desc}</td>
                  <td>
                    <Tooltip title={copied === row.code ? 'Copied!' : 'Copy to clipboard'} arrow>
                      <IconButton
                        size='small'
                        className={copied === row.code ? classes.copiedBtn : classes.copyBtn}
                        onClick={() => handleCopy(row.code)}
                      >
                        {copied === row.code
                          ? <CheckIcon style={{ fontSize: 14 }} />
                          : <FileCopyOutlinedIcon style={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  )
}

InvocationCodesModal.propTypes = {
  code: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
}

export default InvocationCodesModal
